import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { CATALOGUES, DEFAULT_LANGUAGE, LANGUAGES } from './languages';
import { PHRASES } from './phrases';

// Deliberately dependency-free.
//
// react-i18next would be the obvious choice, but it brings plural rules,
// namespaces and a loader we do not need yet. This is the same contract in a
// few lines: t(key), tl(text), language, setLanguage.
//
// HOW THE CHOICE REACHES EVERY SCREEN
// -----------------------------------
// Two layers, both driven by the same `language`:
//   1. t(key) / tl(text) for code that asks for a translation explicitly.
//   2. A page translator: whenever the language is not English, every text
//      node, placeholder, title and aria-label on the page is matched against
//      the phrase dictionary (i18n/phrases.js) and swapped, and a
//      MutationObserver keeps doing that as screens render. Data (names,
//      numbers, amounts) never matches a phrase, so it is never touched, and a
//      phrase that is not in the dictionary simply stays in English.
//
// WHERE THE CHOICE LIVES
// ----------------------
// The company owns the language (CompanyMaster.Language), not the browser.
// Every terminal in the property therefore reads the same screens, and a user
// moving to another PC keeps their language instead of getting English again.
// localStorage is only a MIRROR: it lets the first paint after a reload (and
// the login page, before any company is known) use the right language.

const STORAGE_KEY = 'appLanguage';

const isSupported = (c) => LANGUAGES.some((l) => l.code === c);

const LanguageContext = createContext({
  language: DEFAULT_LANGUAGE,
  setLanguage: () => {},
  languages: LANGUAGES,
  t: (key) => key,
  tl: (text) => text,
});

// Company first (authoritative), then the local mirror, then English.
const readInitial = () => {
  try {
    const company = JSON.parse(sessionStorage.getItem('companyDetails') || 'null');
    const fromCompany = company?.language || company?.Language;
    if (isSupported(fromCompany)) return fromCompany;

    const mirror = localStorage.getItem(STORAGE_KEY);
    if (isSupported(mirror)) return mirror;
  } catch {
    /* fall through to the default */
  }
  return DEFAULT_LANGUAGE;
};

// ── page translator ──────────────────────────────────────────────────────
const SKIP_TAGS = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'TEXTAREA', 'INPUT', 'CODE', 'PRE', 'SVG']);
const ATTRS = ['placeholder', 'title', 'aria-label'];

// Exact phrase after trimming; a trailing ":" or "*" is kept; surrounding
// whitespace is kept, so layout never changes.
function lookup(dict, raw) {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  let hit = dict[trimmed];
  if (hit == null) {
    const m = trimmed.match(/^(.*?)(\s*[:*]+)$/);
    if (m && dict[m[1]] != null) hit = dict[m[1]] + m[2];
  }
  if (hit == null) return null;
  const lead = raw.match(/^\s*/)[0];
  const tail = raw.match(/\s*$/)[0];
  return lead + hit + tail;
}

function skipElement(el) {
  for (let e = el; e && e.nodeType === 1; e = e.parentElement) {
    if (SKIP_TAGS.has(String(e.tagName).toUpperCase())) return true;
    if (e.isContentEditable) return true;
    if (e.hasAttribute('data-notranslate')) return true;
  }
  return false;
}

// A text node remembers the English it was given (__i18nSrc) and what we wrote
// (__i18nOut). React rewrites nodeValue in English whenever it re-renders, so
// "the English" is simply the latest value that is not our own output.
function translateText(node, dict) {
  const cur = node.nodeValue;
  if (!cur || !cur.trim()) return;
  if (node.parentElement && skipElement(node.parentElement)) return;
  const ours = node.__i18nOut != null && cur === node.__i18nOut;
  const source = ours ? node.__i18nSrc : cur;
  if (!dict) {
    if (ours) { node.nodeValue = source; node.__i18nOut = null; }
    return;
  }
  const out = lookup(dict, source);
  if (out == null) {
    if (ours) { node.nodeValue = source; node.__i18nOut = null; }
    return;
  }
  if (cur !== out) {
    node.__i18nSrc = source;
    node.__i18nOut = out;
    node.nodeValue = out;
  }
}

function translateAttrs(el, dict) {
  if (!el.getAttribute || skipElement(el.parentElement || el)) return;
  for (const attr of ATTRS) {
    const cur = el.getAttribute(attr);
    if (!cur) continue;
    const key = `__i18n_${attr}`;
    const st = el[key];
    const ours = st && cur === st.out;
    const source = ours ? st.src : cur;
    if (!dict) {
      if (ours) { el.setAttribute(attr, source); el[key] = null; }
      continue;
    }
    const out = lookup(dict, source);
    if (out == null) {
      if (ours) { el.setAttribute(attr, source); el[key] = null; }
      continue;
    }
    if (cur !== out) {
      el[key] = { src: source, out };
      el.setAttribute(attr, out);
    }
  }
}

function walk(root, dict) {
  if (!root) return;
  if (root.nodeType === 3) { translateText(root, dict); return; }
  if (root.nodeType !== 1) return;
  const tw = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
  const texts = [];
  for (let n = tw.nextNode(); n; n = tw.nextNode()) texts.push(n);
  for (const n of texts) translateText(n, dict);
  translateAttrs(root, dict);
  root.querySelectorAll('[placeholder],[title],[aria-label]').forEach((el) => translateAttrs(el, dict));
}

// Returns a stop() function. English = restore everything and stop watching.
function startPageTranslation(language) {
  const dict = language === DEFAULT_LANGUAGE ? null : PHRASES[language] || null;
  if (typeof document === 'undefined' || !document.body) return () => {};
  walk(document.body, dict);
  document.documentElement.setAttribute('lang', language);
  if (!dict) return () => {};
  const obs = new MutationObserver((muts) => {
    for (const m of muts) {
      if (m.type === 'characterData') translateText(m.target, dict);
      else if (m.type === 'attributes') translateAttrs(m.target, dict);
      else m.addedNodes.forEach((n) => walk(n, dict));
    }
  });
  obs.observe(document.body, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ATTRS });
  return () => obs.disconnect();
}

export function LanguageProvider({ children }) {
  const [language, setLanguageState] = useState(readInitial);

  // Applies the choice locally. Persisting it to the company is the settings
  // screen's (and the login's) job, because only they know whether the save
  // succeeded.
  const setLanguage = useCallback((code) => {
    if (!isSupported(code)) return;
    try {
      localStorage.setItem(STORAGE_KEY, code);

      // Keep the cached company row in step so a reload before the next login
      // does not silently revert the language.
      const raw = sessionStorage.getItem('companyDetails');
      if (raw) {
        const company = JSON.parse(raw);
        company.language = code;
        if ('Language' in company) company.Language = code;
        sessionStorage.setItem('companyDetails', JSON.stringify(company));
      }
    } catch {
      /* private mode / quota - the in-memory choice still applies */
    }
    setLanguageState(code);
  }, []);

  // The page translator follows the language for as long as the app lives.
  useEffect(() => startPageTranslation(language), [language]);

  // A missing key falls back to English, then to the key itself. That means a
  // half-translated language shows English words rather than 'nav.reception',
  // so shipping a partial catalogue is safe.
  const t = useCallback(
    (key, fallback) => {
      const active = CATALOGUES[language] || {};
      return active[key] ?? CATALOGUES.en[key] ?? fallback ?? key;
    },
    [language],
  );

  // Translate a piece of screen text (for strings built in code, like a
  // confirm() message, that the page translator cannot reach).
  const tl = useCallback(
    (text) => (PHRASES[language] || {})[text] ?? text,
    [language],
  );

  const value = useMemo(
    () => ({ language, setLanguage, languages: LANGUAGES, t, tl }),
    [language, setLanguage, t, tl],
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export const useLanguage = () => useContext(LanguageContext);

// Convenience for components that only need the translate function.
export const useT = () => useContext(LanguageContext).t;

// For code that runs outside React (a confirm() in a plain handler): the
// current language from the mirror, and a phrase translated with it.
export const currentLanguage = () => {
  try {
    const m = localStorage.getItem(STORAGE_KEY);
    return isSupported(m) ? m : readInitial();
  } catch {
    return DEFAULT_LANGUAGE;
  }
};
export const tlNow = (text) => (PHRASES[currentLanguage()] || {})[text] ?? text;
